import React, { useState, useEffect, useRef } from "react";
import * as api from "../api";
import { C, R, F, numeric } from "../theme";
import { SERVICES, TESTIMONIALS } from "../constants";
import { Icon, DataIcon } from "../icons";
import TRANSLATIONS from "../translations";
import { Btn, Card, AnimatedNumber } from "./UI";
import PriceEstimator from "./PriceEstimator";

function Landing({ setView }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const t = TRANSLATIONS.fr;
  const [services, setServices] = useState(SERVICES);
  const [branding, setBranding] = useState({ slogan: "Le pressing vient vers vous.", logo_url: "/logo-setal.svg" });
  const [pricing,  setPricing]  = useState([
    { titre: "Lavage au kilo",   prix: "À partir de 300 FCFA/kg",      desc: "Idéal pour le linge courant",              popular: false },
    { titre: "Lavage par pièce", prix: "À partir de 1 000 FCFA/pièce", desc: "Pour chemises, pantalons, robes…",          popular: true  },
    { titre: "Lavage spécial",   prix: "À partir de 3 000 FCFA/pièce", desc: "Costumes, manteaux, vêtements délicats",    popular: false },
  ]);
  const [landingArticles, setLandingArticles] = useState([]);

  const logoClicks = useRef([]);
  function handleLogoClick() {
    const now = Date.now();
    logoClicks.current = [...logoClicks.current, now].filter(t => now - t < 3000);
    if (logoClicks.current.length >= 5) {
      logoClicks.current = [];
      setView("login");
    }
  }

  useEffect(() => {
    api.getServices().then(data => {
      if (data && data.length) setServices(data.map(s => ({ icon: s.icon, label: s.label, description: s.description, price_display: s.price_display || "" })));
    }).catch(() => {});
    api.getPricing().then(data => {
      if (data && data.length) setPricing(data.map(p => ({ titre: p.title, prix: p.price, desc: p.description, popular: p.popular })));
    }).catch(() => {});
    api.getBranding().then(data => {
      if (data) setBranding(prev => ({ ...prev, ...data }));
    }).catch(() => {});
    api.getLandingArticles().then(data => {
      if (data && data.length) setLandingArticles(data);
    }).catch(() => {});
    api.trackPageView("/");
  }, []);

  // Révélation au défilement
  const [visibleSections, setVisibleSections] = useState({});
  const sectionRefs = useRef({});
  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          setVisibleSections(prev => ({ ...prev, [entry.target.dataset.section]: true }));
        }
      });
    }, { threshold: 0.12 });
    Object.values(sectionRefs.current).forEach(ref => {
      if (ref) observer.observe(ref);
    });
    return () => observer.disconnect();
  }, [landingArticles]);

  const setSectionRef = (name) => (el) => {
    if (el) { el.dataset.section = name; sectionRefs.current[name] = el; }
  };

  // Apparition sobre : léger décalage vertical, pas de mise à l'échelle
  const reveal = (name, delay = 0) => ({
    opacity: visibleSections[name] ? 1 : 0,
    transform: visibleSections[name] ? "none" : "translateY(10px)",
    transition: `opacity .4s ease ${delay}s, transform .4s ease ${delay}s`,
  });

  const sectionTitle = {
    fontSize: "clamp(21px, 3.4vw, 27px)",
    fontWeight: F.weight.bold,
    color: C.ink,
    letterSpacing: "-.02em",
    marginBottom: 6,
  };
  const sectionSub = { color: C.inkMuted, fontSize: F.size.lg, maxWidth: 520 };

  const navLinks = [
    { icon: "order", label: t.commander,  view: "order" },
    { icon: "track", label: t.suivre,     view: "track" },
    { icon: "user",  label: "Mon espace", view: "client_space" },
  ];

  return (
    <div style={{ paddingBottom: 60, background: C.surface }}>
      {/* ─── Barre de navigation ─────────────────────────────────────────── */}
      <nav style={{
        position: "sticky", top: 0, zIndex: 100,
        background: C.surface,
        borderBottom: `1px solid ${C.border}`,
        padding: "0 20px",
        display: "flex", alignItems: "center", justifyContent: "space-between",
        height: 58,
      }}>
        <div
          style={{ display: "flex", alignItems: "center", gap: 9, userSelect: "none" }}
          onClick={handleLogoClick}
        >
          <span style={{
            width: 26, height: 26, borderRadius: R.md, background: C.navy,
            display: "flex", alignItems: "center", justifyContent: "center", color: "#fff",
          }}>
            <Icon name="washing" size={15} strokeWidth={2} />
          </span>
          <span style={{ fontWeight: F.weight.bold, fontSize: F.size.lg, color: C.ink, letterSpacing: "-.02em" }}>
            Setal Express
          </span>
        </div>

        <div className="nav-desktop" style={{ display: "flex", gap: 6, alignItems: "center" }}>
          <Btn variant="subtle" small icon="track" onClick={() => setView("track")}>{t.suivre}</Btn>
          <Btn variant="subtle" small icon="user" onClick={() => setView("client_space")}>Mon espace</Btn>
          <Btn variant="primary" small icon="order" onClick={() => setView("order")}>{t.commander}</Btn>
        </div>

        <button
          className="nav-hamburger"
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label={menuOpen ? "Fermer le menu" : "Ouvrir le menu"}
          aria-expanded={menuOpen}
          style={{
            display: "none", alignItems: "center", justifyContent: "center",
            width: 36, height: 36, borderRadius: R.md, color: C.ink,
            border: `1px solid ${C.border}`,
          }}
        >
          <Icon name={menuOpen ? "close" : "menu"} size={18} />
        </button>
      </nav>

      {/* ─── Menu mobile ─────────────────────────────────────────────────── */}
      {menuOpen && (
        <div className="nav-mobile-menu" style={{
          position: "sticky", top: 58, zIndex: 99,
          background: C.surface, borderBottom: `1px solid ${C.border}`,
          padding: 12, display: "flex", flexDirection: "column", gap: 6,
          animation: "slideDown .18s ease",
        }}>
          {navLinks.map(l => (
            <button
              key={l.view}
              onClick={() => { setMenuOpen(false); setView(l.view); }}
              style={{
                display: "flex", alignItems: "center", gap: 10,
                padding: "11px 12px", borderRadius: R.md,
                fontSize: F.size.md, fontWeight: F.weight.medium, color: C.ink,
                textAlign: "left", width: "100%",
              }}
            >
              <Icon name={l.icon} size={17} color={C.inkMuted} />
              {l.label}
            </button>
          ))}
        </div>
      )}

      {/* ─── Bannière principale ─────────────────────────────────────────── */}
      <header style={{ background: C.navy, color: "#fff", padding: "68px 20px 0" }}>
        <div style={{ maxWidth: 940, margin: "0 auto" }}>
          <span style={{
            display: "inline-flex", alignItems: "center", gap: 6,
            border: "1px solid rgba(255,255,255,.22)", borderRadius: R.pill,
            padding: "4px 11px", fontSize: F.size.sm, fontWeight: F.weight.medium,
            color: "rgba(255,255,255,.9)", marginBottom: 20,
          }}>
            <Icon name="location" size={12} />
            Dakar · 7j/7 · 9h – 21h
          </span>

          <h1 style={{
            fontSize: "clamp(30px, 6vw, 50px)",
            fontWeight: F.weight.bold, lineHeight: 1.08,
            letterSpacing: "-.03em", marginBottom: 16, maxWidth: 620,
          }}>
            {branding.slogan}
          </h1>

          <p style={{
            fontSize: "clamp(15px, 2.2vw, 18px)", lineHeight: 1.6,
            color: "rgba(255,255,255,.72)", maxWidth: 500, marginBottom: 28,
          }}>
            {t.heroSubtitle} Nous collectons chez vous, nous nettoyons, nous livrons.
          </p>

          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 44 }}>
            <Btn variant="white" icon="order" onClick={() => setView("order")}
              style={{ padding: "11px 20px", fontSize: F.size.lg }}>
              {t.orderBtn}
            </Btn>
            <Btn icon="track" onClick={() => setView("track")}
              style={{
                padding: "11px 20px", fontSize: F.size.lg,
                background: "transparent", color: "#fff", borderColor: "rgba(255,255,255,.3)",
              }}>
              {t.trackBtn}
            </Btn>
          </div>

          {/* Chiffres clés — grille à 3 colonnes : pas de séparateur orphelin
              quand la ligne se replie sur mobile. */}
          <div style={{
            display: "grid", gridTemplateColumns: "repeat(3, 1fr)",
            borderTop: "1px solid rgba(255,255,255,.14)",
          }}>
            {[[t.stats1, t.stats1Label], [t.stats2, t.stats2Label], [t.stats3, t.stats3Label]].map(([n, l], i) => (
              <div key={l} style={{
                padding: "18px 14px 22px",
                paddingLeft: i > 0 ? 16 : 0,
                borderLeft: i > 0 ? "1px solid rgba(255,255,255,.14)" : "none",
                minWidth: 0,
              }}>
                <div style={{
                  fontSize: "clamp(20px, 5vw, 26px)", fontWeight: F.weight.bold,
                  letterSpacing: "-.02em", ...numeric,
                }}>
                  <AnimatedNumber value={parseInt(n) || n} />
                  {String(n).replace(/\d/g, "")}
                </div>
                <div style={{
                  fontSize: F.size.base, color: "rgba(255,255,255,.6)",
                  marginTop: 3, lineHeight: 1.35,
                }}>{l}</div>
              </div>
            ))}
          </div>
        </div>
      </header>

      {/* ─── Tarifs par article ──────────────────────────────────────────── */}
      {landingArticles.length > 0 && (
        <section ref={setSectionRef("articles")} style={{ background: C.bg, borderBottom: `1px solid ${C.border}`, padding: "52px 20px" }}>
          <div style={{ maxWidth: 940, margin: "0 auto" }}>
            <div style={{ marginBottom: 26, ...reveal("articles") }}>
              <h2 style={sectionTitle}>Nos articles et leurs prix</h2>
              <p style={sectionSub}>Des tarifs clairs, affichés à l'avance. Pas de surprise à la livraison.</p>
            </div>

            <div style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(148px, 1fr))",
              gap: 10,
            }}>
              {landingArticles.map((art, i) => (
                <div key={art.id} style={{
                  background: C.surface,
                  border: `1px solid ${C.border}`,
                  borderRadius: R.lg,
                  padding: "14px 14px 13px",
                  display: "flex", flexDirection: "column", gap: 9,
                  ...reveal("articles", Math.min(i * 0.025, 0.3)),
                }}>
                  <span style={{
                    width: 30, height: 30, borderRadius: R.md,
                    background: C.navySoft, color: C.navy,
                    display: "flex", alignItems: "center", justifyContent: "center",
                  }}>
                    <DataIcon value={art.icon} size={16} />
                  </span>
                  <div style={{
                    fontWeight: F.weight.medium, fontSize: F.size.md, color: C.ink,
                    lineHeight: 1.35, flex: 1,
                  }}>
                    {art.display_name || art.name}
                  </div>
                  <div style={{
                    fontSize: F.size.lg, fontWeight: F.weight.bold, color: C.ink,
                    letterSpacing: "-.02em", ...numeric,
                  }}>
                    {Number(art.price).toLocaleString("fr-FR")}
                    <span style={{ fontSize: F.size.sm, fontWeight: F.weight.medium, color: C.inkMuted }}> FCFA</span>
                  </div>
                </div>
              ))}
            </div>

            <div style={{ marginTop: 24, ...reveal("articles", 0.25) }}>
              <Btn icon="order" onClick={() => setView("order")} style={{ padding: "11px 20px", fontSize: F.size.lg }}>
                Commander maintenant
              </Btn>
            </div>
          </div>
        </section>
      )}

      {/* ─── Services ────────────────────────────────────────────────────── */}
      <section ref={setSectionRef("services")} style={{ maxWidth: 940, margin: "0 auto", padding: "52px 20px" }}>
        <div style={{ marginBottom: 26, ...reveal("services") }}>
          <h2 style={sectionTitle}>{t.servicesTitle}</h2>
          <p style={sectionSub}>{t.servicesSubtitle}</p>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 12 }}>
          {services.map((s, i) => (
            <Card key={s.label} hover style={{ ...reveal("services", i * 0.05) }}>
              <span style={{
                width: 32, height: 32, borderRadius: R.md,
                background: C.navySoft, color: C.navy,
                display: "flex", alignItems: "center", justifyContent: "center",
                marginBottom: 11,
              }}>
                <DataIcon value={s.icon} size={17} />
              </span>
              <div style={{ fontWeight: F.weight.semi, fontSize: F.size.md, color: C.ink }}>{s.label}</div>
              {s.description && (
                <div style={{ fontSize: F.size.base, color: C.inkMuted, marginTop: 4, lineHeight: 1.55 }}>{s.description}</div>
              )}
              {s.price_display && (
                <div style={{
                  display: "inline-block", marginTop: 10,
                  fontSize: F.size.base, fontWeight: F.weight.semi, color: C.navy,
                  background: C.navySoft, borderRadius: R.sm, padding: "3px 8px", ...numeric,
                }}>
                  {s.price_display}
                </div>
              )}
            </Card>
          ))}
        </div>
      </section>

      {/* ─── Comment ça marche ───────────────────────────────────────────── */}
      <section ref={setSectionRef("how")} style={{
        background: C.bg, borderTop: `1px solid ${C.border}`, borderBottom: `1px solid ${C.border}`,
        padding: "52px 20px",
      }}>
        <div style={{ maxWidth: 940, margin: "0 auto" }}>
          <div style={{ marginBottom: 26, ...reveal("how") }}>
            <h2 style={sectionTitle}>{t.howTitle}</h2>
            <p style={sectionSub}>{t.howSubtitle}</p>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))", gap: 12 }}>
            {t.howSteps.map(([n, icon, title, desc], i) => (
              <div key={n} style={{
                background: C.surface, border: `1px solid ${C.border}`, borderRadius: R.lg,
                padding: 16, ...reveal("how", i * 0.06),
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: 9, marginBottom: 10 }}>
                  <span style={{
                    width: 22, height: 22, borderRadius: R.sm,
                    background: C.navy, color: "#fff",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: F.size.sm, fontWeight: F.weight.bold, ...numeric,
                  }}>{n}</span>
                  <Icon name={icon} size={17} color={C.inkMuted} />
                </div>
                <div style={{ fontWeight: F.weight.semi, fontSize: F.size.md, color: C.ink }}>{title}</div>
                <div style={{ fontSize: F.size.base, color: C.inkMuted, marginTop: 3, lineHeight: 1.55 }}>{desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Estimateur ──────────────────────────────────────────────────── */}
      <PriceEstimator t={t} pricing={pricing} setView={setView} />

      {/* ─── Formules ────────────────────────────────────────────────────── */}
      <section ref={setSectionRef("pricing")} style={{ maxWidth: 940, margin: "0 auto", padding: "52px 20px" }}>
        <div style={{ marginBottom: 26, ...reveal("pricing") }}>
          <h2 style={sectionTitle}>{t.pricingTitle}</h2>
          <p style={sectionSub}>{t.pricingSubtitle}</p>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(230px, 1fr))", gap: 12 }}>
          {pricing.map((p, i) => (
            <div key={p.titre} style={{
              border: `1px solid ${p.popular ? C.navy : C.border}`,
              borderRadius: R.lg, padding: 18, background: C.surface,
              position: "relative", ...reveal("pricing", i * 0.06),
            }}>
              {p.popular && (
                <span style={{
                  position: "absolute", top: -9, left: 18,
                  display: "inline-flex", alignItems: "center", gap: 4,
                  background: C.navy, color: "#fff",
                  borderRadius: R.sm, padding: "2px 8px",
                  fontSize: F.size.xs, fontWeight: F.weight.semi,
                }}>
                  <Icon name="star" size={10} />
                  Le plus choisi
                </span>
              )}
              <div style={{ fontWeight: F.weight.semi, fontSize: F.size.md, color: C.ink, marginBottom: 7 }}>
                {p.titre || p.title}
              </div>
              <div style={{
                fontSize: F.size["2xl"], fontWeight: F.weight.bold, color: C.ink,
                letterSpacing: "-.02em", marginBottom: 7, ...numeric,
              }}>
                {p.prix || p.price}
              </div>
              <div style={{ fontSize: F.size.base, color: C.inkMuted, lineHeight: 1.55 }}>
                {p.desc || p.description}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ─── Avis clients ────────────────────────────────────────────────── */}
      <section ref={setSectionRef("testimonials")} style={{
        background: C.bg, borderTop: `1px solid ${C.border}`, padding: "52px 20px",
      }}>
        <div style={{ maxWidth: 940, margin: "0 auto" }}>
          <div style={{ marginBottom: 26, ...reveal("testimonials") }}>
            <h2 style={sectionTitle}>{t.testimonialsTitle}</h2>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 12 }}>
            {TESTIMONIALS.map((tm, i) => (
              <Card key={tm.name} style={{ ...reveal("testimonials", i * 0.06) }}>
                <div style={{ display: "flex", gap: 2, marginBottom: 11 }}>
                  {Array.from({ length: tm.stars }, (_, k) => (
                    <Icon key={k} name="star" size={13} color={C.amber} />
                  ))}
                </div>
                <p style={{ color: C.inkBody, lineHeight: 1.6, fontSize: F.size.md, marginBottom: 13 }}>
                  {tm.text}
                </p>
                <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
                  <span style={{
                    width: 28, height: 28, borderRadius: "50%",
                    background: C.navySoft, color: C.navy,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontWeight: F.weight.semi, fontSize: F.size.base,
                  }}>{tm.name[0]}</span>
                  <span style={{ fontWeight: F.weight.medium, color: C.ink, fontSize: F.size.base }}>{tm.name}</span>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Appel à l'action ────────────────────────────────────────────── */}
      <section style={{ background: C.navy, color: "#fff", padding: "52px 20px" }}>
        <div style={{
          maxWidth: 940, margin: "0 auto",
          display: "flex", alignItems: "center", justifyContent: "space-between",
          gap: 20, flexWrap: "wrap",
        }}>
          <div>
            <h2 style={{
              fontSize: "clamp(21px, 3.4vw, 27px)", fontWeight: F.weight.bold,
              letterSpacing: "-.02em", marginBottom: 6,
            }}>
              {t.ctaTitle}
            </h2>
            <p style={{ color: "rgba(255,255,255,.7)", fontSize: F.size.lg }}>{t.ctaSubtitle}</p>
          </div>
          <Btn variant="white" icon="order" onClick={() => setView("order")}
            style={{ padding: "11px 20px", fontSize: F.size.lg }}>
            {t.orderBtn}
          </Btn>
        </div>
      </section>

      {/* ─── Pied de page ────────────────────────────────────────────────── */}
      <footer style={{ background: "#061a2e", color: "rgba(255,255,255,.62)", padding: "40px 20px 22px" }}>
        <div style={{ maxWidth: 940, margin: "0 auto" }}>
          <div style={{
            display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
            gap: 28, marginBottom: 28,
          }}>
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 9, marginBottom: 11 }}>
                <span style={{
                  width: 24, height: 24, borderRadius: R.md, background: "rgba(255,255,255,.1)",
                  display: "flex", alignItems: "center", justifyContent: "center", color: "#fff",
                }}>
                  <Icon name="washing" size={14} />
                </span>
                <span style={{ fontWeight: F.weight.bold, fontSize: F.size.lg, color: "#fff" }}>Setal Express</span>
              </div>
              <p style={{ fontSize: F.size.base, lineHeight: 1.65 }}>
                Service de pressing à Dakar. Collecte à domicile, nettoyage professionnel
                et livraison rapide.
              </p>
            </div>

            <div>
              <div style={{
                fontWeight: F.weight.semi, color: "#fff", fontSize: F.size.base,
                marginBottom: 11, letterSpacing: ".03em", textTransform: "uppercase",
              }}>Navigation</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {navLinks.map(l => (
                  <button key={l.view} onClick={() => setView(l.view)} style={{
                    color: "rgba(255,255,255,.72)", fontSize: F.size.md,
                    textAlign: "left", display: "flex", alignItems: "center", gap: 8,
                  }}>
                    <Icon name={l.icon} size={14} />
                    {l.label}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <div style={{
                fontWeight: F.weight.semi, color: "#fff", fontSize: F.size.base,
                marginBottom: 11, letterSpacing: ".03em", textTransform: "uppercase",
              }}>Contact</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 8, fontSize: F.size.md }}>
                <a href={`tel:${(branding.phone_main || "+221 76 809 09 28").replace(/\s/g, "")}`}
                  style={{ color: "rgba(255,255,255,.72)", display: "flex", alignItems: "center", gap: 8 }}>
                  <Icon name="phone" size={14} />
                  <span style={numeric}>{branding.phone_main || "+221 76 809 09 28"}</span>
                </a>
                <a href={`https://wa.me/${branding.phone_whatsapp || "221768090928"}`} target="_blank" rel="noreferrer"
                  style={{ color: "rgba(255,255,255,.72)", display: "flex", alignItems: "center", gap: 8 }}>
                  <Icon name="chat" size={14} />
                  WhatsApp
                </a>
                {branding.phones_extra && branding.phones_extra.trim().split("\n").filter(Boolean).map((line, i) => (
                  <span key={i} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <Icon name="mobile" size={14} />
                    <span style={numeric}>{line}</span>
                  </span>
                ))}
                <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <Icon name="location" size={14} />
                  Dakar, Sénégal
                </span>
                <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <Icon name="clock" size={14} />
                  7j/7 · 9h – 21h
                </span>
              </div>
            </div>
          </div>

          <div style={{
            borderTop: "1px solid rgba(255,255,255,.1)", paddingTop: 18,
            display: "flex", justifyContent: "space-between", alignItems: "center",
            flexWrap: "wrap", gap: 12,
          }}>
            <p style={{ fontSize: F.size.base }}>© 2026 Setal Express · Tous droits réservés</p>
            <div style={{ display: "flex", gap: 6 }}>
              {[
                { href: `https://wa.me/${branding.phone_whatsapp || "221768090928"}`, icon: "chat",  title: "WhatsApp", ext: true },
                { href: `tel:${(branding.phone_main || "+221 76 809 09 28").replace(/\s/g, "")}`,   icon: "phone", title: "Appeler" },
                { href: "mailto:contact@setalexpress.com",                                          icon: "mail",  title: "Courriel" },
              ].map(s => (
                <a key={s.icon} href={s.href} title={s.title} aria-label={s.title}
                  target={s.ext ? "_blank" : undefined} rel={s.ext ? "noreferrer" : undefined}
                  style={{
                    width: 30, height: 30, borderRadius: R.md,
                    border: "1px solid rgba(255,255,255,.14)",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    color: "rgba(255,255,255,.72)",
                  }}>
                  <Icon name={s.icon} size={14} />
                </a>
              ))}
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default Landing;
